import { Link } from "react-router-dom";
import { usePageMeta } from "@/lib/seo";
import { STORE_ADDRESS, STORE_PHONE, STORE_HOURS } from "@/constants/store";
import { Truck, Tag, Store, Package } from "lucide-react";

const SOURCES = [
  { icon: Truck, t: "Truckload overstock", d: "Whole truckloads of brand-new overstock straight from major retailers." },
  { icon: Tag, t: "Closeouts & shelf pulls", d: "Discontinued lines and shelf pulls bought below wholesale." },
  { icon: Store, t: "Store closings", d: "Full inventories from stores that shut their doors." },
  { icon: Package, t: "Direct wholesale", d: "Direct deals with wholesalers and manufacturers." },
];

export default function AboutPage() {
  usePageMeta(
    "Liquidation Pallets, Closeouts & Overstock in Grand Rapids — How We Buy, How We Price",
    "IPVALUE MARKETPLACE is Grand Rapids' closeout & liquidation store: wholesale pallets, overstock furniture, mattresses, appliances & e-bikes at closeout prices. 88 54th St SW, Suite 103, Grand Rapids, MI 49548."
  );
  return (
    <div data-testid="about-page">
      <section className="bg-[#0A1C40] border-b-4 border-[#FFC220]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-14">
          <div className="ipv-eyebrow text-[#FFC220]">About IPVALUE MARKETPLACE</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white mt-3">
            How we buy.<br />How we price.
          </h1>
          <p className="text-white/70 mt-4 max-w-xl">
            Four sourcing methods keep our prices low — and honest tagging keeps them trustworthy. New loads
            arrive every week.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SOURCES.map((s) => (
            <div key={s.t} className="border-2 border-gray-200 p-5">
              <s.icon className="w-6 h-6 text-[#C9971E]" />
              <div className="font-display font-bold mt-3">{s.t}</div>
              <div className="text-sm text-gray-600 mt-1">{s.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="bg-[#FFC220] p-6" data-testid="about-gold-tag">
            <div className="font-display font-black text-xl text-[#0A1C40]">★ The Gold Tag</div>
            <p className="text-sm text-[#0A1C40]/85 mt-2 leading-relaxed">
              Our deepest deals. The tag shows what the item sells for elsewhere right next to our price.
              Gold-tag items move fast and usually can't be restocked — when they're gone, they're gone.
            </p>
          </div>
          <div className="border-2 border-[#0A1C40] p-6" data-testid="about-honest-tagging">
            <div className="font-display font-black text-xl text-[#0A1C40]">Every item tagged honestly</div>
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">
              NEW, OPEN BOX, or AS-IS — every item says what it is. And never a made-up retail price to inflate
              a deal: every item is priced on its own merit. No mark-up-to-mark-down games.
            </p>
          </div>
        </div>

        <div className="mt-8 border-2 border-gray-200 p-6" data-testid="about-seo-blurb">
          <div className="font-display font-black text-xl text-[#0A1C40]">
            Grand Rapids&apos; closeout, overstock &amp; liquidation store
          </div>
          <p className="text-sm text-gray-700 mt-2 leading-relaxed">
            Searching for <b>liquidation pallets near Grand Rapids</b>, <b>closeout pallets in Michigan</b>, or
            <b> wholesale pallets in West Michigan</b>? That&apos;s us. IPVALUE MARKETPLACE stocks manifested and
            unmanifested closeout &amp; overstock pallets, plus brand-new overstock furniture, sectionals, recliners,
            mattresses, appliances, e-bikes and scooters — all at closeout prices. Shoppers and resellers from
            Grand Rapids, Wyoming, Kentwood, Grandville, Byron Center and across West Michigan buy from our
            showroom at {STORE_ADDRESS.short}. Want a pallet built to your list? Use our{" "}
            <Link to="/build-pallet" className="underline font-bold">Build-Your-Own Pallet</Link> program — pick your
            items from live in-stock availability.
          </p>
        </div>

        <div className="mt-8 bg-gray-50 border border-gray-200 p-6 text-sm text-gray-700 leading-relaxed">
          <b>New loads arrive every week.</b> Visit us at {STORE_ADDRESS.short} ({STORE_HOURS.display}), call{" "}
          {STORE_PHONE}, or <Link to="/shop" className="underline font-bold">shop online</Link> — items marked
          "Available in store now" are on the floor today, and "Ships to you" items travel straight to your door.
          Reselling? <Link to="/resellers" className="underline font-bold">Join the Reseller Program</Link>.
        </div>
      </section>
    </div>
  );
}
